<template>
  <v-row justify="center">
    <v-col cols="12" sm="8" md="6">
      <v-card class="pa-5" outlined>
        <v-card-title>
          <h1>Owner Signup</h1>
        </v-card-title>
        <v-card-text>
          <v-form
            @submit.prevent="maybeSignup"
            v-model="isFormValid"
            ref="form"
            class="admin-signup-form"
          >
            <h3>Please login to edit your store settings</h3>
            <v-text-field
              v-model="name"
              class="mt-4"
              color="blue darken-1"
              label="Your Name"
              large
              :rules="[requiredRule]"
              ref="name"
              autofocus
            />
            <v-text-field
              v-model="email"
              class="email-input mt-4"
              color="blue darken-1"
              label="Email Address"
              large
              :rules="[emailRule]"
              :error-messages="emailAvailabilityError"
              ref="email"
              :loading="isSearchingEmail"
              @input="emailChangeFn"
            >
              <template #append v-if="!emailRule(email).length">
                <transition name="fade" mode="out-in">
                  <v-icon v-if="isEmailTaken" color="red" right class="mt-4"
                    >mdi-close-circle</v-icon
                  >
                  <v-icon v-else color="green" right class="mt-4"
                    >mdi-check-circle</v-icon
                  >
                </transition>
              </template>
            </v-text-field>
            <v-text-field
              v-model="password"
              class="password-input mt-4 mb-2"
              color="blue darken-1"
              label="Password"
              loading
              large
              :rules="[requiredRule, passStrengthRule]"
              ref="password"
              :type="showPass ? 'text' : 'password'"
            >
              <template #progress>
                <v-progress-linear
                  :value="passLength"
                  :color="color"
                  absolute
                  height="3"
                ></v-progress-linear>
              </template>
              <template #append>
                <v-icon class="mt-4" @click="showPass = !showPass">{{
                  showPass ? 'mdi-eye' : 'mdi-eye-off'
                }}</v-icon>
              </template>
            </v-text-field>
            <transition name="fade" mode="out-in">
              <div v-if="error" class="red--text mb-2">{{ error }}</div>
            </transition>
            <v-btn
              color="amber darken-3"
              :disabled="!isFormValid || isSearchingEmail"
              class="white--text"
              :loading="loading"
              depressed
              x-large
              type="submit"
              >Register</v-btn
            >
            <h3 class="mt-4">Already have an account?</h3>
            <v-btn
              color="blue"
              depressed
              exact
              class="white--text mt-2"
              :disabled="loading"
              :to="{ name: merchantLoginRoute }"
              >Log in</v-btn
            >
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions } from 'vuex'
import { EMAIL_IN_USE } from '@/constants/ApiMessages'
import debounce from 'debounce'
import { MerchantRoutes } from '@/constants/RouteNames'

export default {
  name: 'AdminSignupView',
  data: () => {
    return {
      isFormValid: false,
      loading: false,
      error: false,
      name: '',
      email: '',
      password: '',
      showPass: false,
      isEmailAvailable: true,
      isSearchingEmail: false,
      requiredRule: (v) => !!v.trim() || 'This field is required',
      trueRule: (v) => v || 'This field is required',
      emailRule: (email) => {
        var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return re.test(email) || 'Please enter a valid email'
      },
      passStrengthRule: (password) => {
        // https://gist.github.com/HarishChaudhari/0dd5514ce430991a1b1b8fa04e8b72a4
        var regex = /^(?=.*[A-Z])(?=.*[a-z])((?=.*[\d])|(?=.*[!@#$%^&*]))[\w!@#$%^&*]{8,}$/
        return (
          regex.test(password) ||
          `Passwords must be: - At least 8 characters long
             - Include at least 1 lowercase letter
             - 1 capital letter
             - 1 number or special character "!@#$%^&*"`
        )
      },
      errorMessages: {
        [EMAIL_IN_USE]: `That email address is taken. Please choose a different one or login using your account`,
      },
      merchantLoginRoute: MerchantRoutes.LOGIN,
    }
  },
  computed: {
    emailAvailabilityError() {
      return this.isEmailAvailable ? null : this.errorMessages.EMAIL_IN_USE
    },
    isEmailTaken() {
      if (!this.email.length || !this.$refs.email) {
        return false
      }
      return !this.$refs.email.valid ? false : this.emailAvailabilityError
    },
    passLength() {
      return (this.password.length / 8) * 100
    },
    color() {
      let passwordIsValid = this.$refs.password
        ? this.$refs.password.validate()
        : false
      let colorOpts = ['error', 'warning']
      if (passwordIsValid) {
        colorOpts.push('success')
      }
      let index = Math.min(
        colorOpts.length - 1,
        Math.floor(this.passLength / 40)
      )
      return colorOpts[index]
    },
  },
  mounted() {
    this.$refs.email.$el.focus()
  },
  methods: {
    ...mapActions('auth', ['signup', 'checkEmailAvailability']),
    async maybeSignup() {
      this.loading = true
      this.error = false
      let { name, email, password } = this

      let formIsValid = await this.$refs.form.validate()
      if (!formIsValid) {
        return false
      }

      try {
        let admin = await this.signup({ name, email, password })
        if (admin) {
          let next = {
            name:
              this.$route.query.loginRoute ||
              MerchantRoutes.Manage.SHOP_SETTINGS,
          }
          this.$router.push(next)
        }
      } catch (error) {
        // this.error = `Sorry we couldn't find an account registered to that email.`
        this.error = this.errorMessages[error.response.data]
      } finally {
        this.loading = false
      }
    }, // maybeSignup

    emailChangeFn: debounce(async function() {
      this.isEmailAvailable = true
      let isEmailValid = await this.$refs.email.validate()
      this.$nextTick(async () => {
        if (isEmailValid) {
          this.isSearchingEmail = true
          let emailExists = await this.checkEmailAvailability(this.email)
          this.isEmailAvailable = !emailExists
          this.isSearchingEmail = false
        }
      })
    }, 250), // emailChangeFn
  }, // methods
  beforeDestroy() {
    this.loading = false
    this.error = false
  },
}
</script>

<style lang="css">
.admin-signup-form input {
  font-size: 2em;
  line-height: 1.3;
  max-height: none;
}
.admin-signup-form .v-input .error--text .v-messages__message {
  font-size: 1.2em;
  line-height: 1.3;
}
</style>
